import {listModeloTareasByTitlePrefixes} from 'src/modules/tarea/buildBlankProcedureListFromModelo';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {isId23ModeloTareaTitle} from 'src/modules/tarea/medicionesParcialId23';
import {isId24ModeloTareaTitle} from 'src/modules/tarea/medicionesParcialId24';
import ModeloTareaService from 'src/modules/modeloTarea/modeloTareaService';
import {
  normalizeParentFieldLayoutConfig,
  ParentFieldLayoutConfig,
} from 'src/modules/tarea/parentFieldLayout';
import {buildParcialGroups, getRecordId} from 'src/modules/tarea/tareaParcialHelpers';

export const getTableViewConfigFromModelo = (modeloTarea: any) =>
  modeloTarea?.appFields?.childTasksTableView || {};

export const getTableViewConfig = (task: any) => getTableViewConfigFromModelo(task?.modeloTarea);

/** Modelo hijo con la tabla de más columnas (misma regla que buildParcialGroups). */
export const getWidestChildModelo = (childTasks: any[] = []) =>
  buildParcialGroups(childTasks)[0]?.modelo || null;

/** Key del mapa (otros tenants): id modelo padre, o tituloTarea como fallback. */
export const getParentConfigKey = (parentTask: any) => {
  const parentModelId = getRecordId(parentTask?.modeloTarea);
  if (parentModelId) return parentModelId;
  const titulo = String(parentTask?.tituloTarea || '').trim();
  return titulo || '';
};

/** Keys permitidas en Plastyverg para parentQuestionKeysByParentModel. */
export const PLASTYVERG_PARENT_FIELDS_MAP_KEYS = ['ID-23', 'ID-24'] as const;
export type PlastyvergParentFieldsMapKey = (typeof PLASTYVERG_PARENT_FIELDS_MAP_KEYS)[number];

export const isPlastyvergParentFieldsMapKey = (key: unknown): key is PlastyvergParentFieldsMapKey =>
  key === 'ID-23' || key === 'ID-24';

/**
 * Key de persistencia/lectura del layout:
 * - Plastyverg: `ID-23` / `ID-24` (Ext/Sel o widest).
 * - Otros tenants: modelo padre / tituloTarea.
 */
export const resolveParentFieldsMapKey = (
  parentTask: any,
  childTasks: any[] = [],
  tenantId?: string | null,
  layoutMapKey?: PlastyvergParentFieldsMapKey | null,
): string => {
  if (isPlastyvergTenant(tenantId)) {
    if (layoutMapKey && isPlastyvergParentFieldsMapKey(layoutMapKey)) return layoutMapKey;
    const prefix = resolvePlastyvergCamposSeleccionadosPrefix(parentTask, childTasks);
    if (prefix) return prefix;
    const widestTitulo = getWidestChildModelo(childTasks)?.titulo;
    if (isId24ModeloTareaTitle(widestTitulo) && !isId23ModeloTareaTitle(widestTitulo)) return 'ID-24';
    if (isId23ModeloTareaTitle(widestTitulo)) return 'ID-23';
    // Plastyverg no indexa por tituloTarea/modelo padre.
    return '';
  }
  return getParentConfigKey(parentTask);
};

/** En Plastyverg deja solo entradas ID-23 / ID-24 (limpia keys legacy de padre/titulo). */
export const sanitizePlastyvergParentFieldsMap = (
  byParentModel: Record<string, unknown> = {},
): Record<string, unknown> => {
  const next: Record<string, unknown> = {};
  for (const key of PLASTYVERG_PARENT_FIELDS_MAP_KEYS) {
    if (byParentModel[key] != null) next[key] = byParentModel[key];
  }
  return next;
};

export const isOtPremiumMetaFlagOn = (value: unknown) => {
  if (value === 1 || value === '1' || value === true) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'si' || normalized === 'sí' || normalized === 'yes' || normalized === 'true';
  }
  return false;
};

export const isOtPremiumParent = (task: any) =>
  Boolean(
    task?.otPremium === true ||
      String(task?.isTaskSourceKey || '').startsWith('otPremium:') ||
      task?.isTaskSourceMeta?.source === 'otPremium',
  );

const childModeloTitulo = (task: any) =>
  task?.modeloTarea?.titulo || task?.tituloTarea || task?.titulo || task?.label || '';

export const getChildInspectionType = (task: any): PlastyvergParentFieldsMapKey | null => {
  const titulo = childModeloTitulo(task);
  const is23 = isId23ModeloTareaTitle(titulo);
  const is24 = isId24ModeloTareaTitle(titulo);
  if (is24 && !is23) return 'ID-24';
  if (is23) return 'ID-23';
  return null;
};

export const filterChildrenByInspectionTab = (
  children: any[] = [],
  tab?: PlastyvergParentFieldsMapKey | null,
) => {
  if (!tab) return children;
  return children.filter((child) => getChildInspectionType(child) === tab);
};

/** Tabs ID-23/ID-24: OT Premium Plastyverg, unión de flags Ext/Sel y hijas existentes. */
export const resolveOtPremiumInspectionTabs = (
  parentTask: any,
  childTasks: any[] = [],
  tenantId?: string | null,
): PlastyvergParentFieldsMapKey[] => {
  if (!isPlastyvergTenant(tenantId) || !isOtPremiumParent(parentTask)) return [];
  const data = parentTask?.isTaskSourceMeta?.data;
  const extOn = isOtPremiumMetaFlagOn(data?.Ext);
  const selOn = isOtPremiumMetaFlagOn(data?.Sel);
  const has23 = childTasks.some((child) => getChildInspectionType(child) === 'ID-23');
  const has24 = childTasks.some((child) => getChildInspectionType(child) === 'ID-24');
  const tabs: PlastyvergParentFieldsMapKey[] = [];
  if (extOn || has23) tabs.push('ID-23');
  if (selOn || has24) tabs.push('ID-24');
  return tabs;
};

export const hasUnverifiedChildrenOfInspectionType = (
  eligibleChildren: any[] = [],
  modeloTitulo?: string | null,
) => {
  const type = getChildInspectionType({tituloTarea: modeloTitulo, titulo: modeloTitulo});
  if (!type) return eligibleChildren.length > 0;
  return filterChildrenByInspectionTab(eligibleChildren, type).length > 0;
};

/** Une meta/modelo del find con el record del form (a veces el listado trae meta y el find tarda). */
export const mergeParentTaskForConfig = (detailedParent: any, parentTask: any) => ({
  ...(parentTask || {}),
  ...(detailedParent || {}),
  isTaskSourceMeta: detailedParent?.isTaskSourceMeta || parentTask?.isTaskSourceMeta,
  modeloTarea: detailedParent?.modeloTarea || parentTask?.modeloTarea,
  tituloTarea: detailedParent?.tituloTarea || parentTask?.tituloTarea,
  listaProcedimiento: detailedParent?.listaProcedimiento || parentTask?.listaProcedimiento,
});

/**
 * Plastyverg OT Premium: Ext → ID-23, Sel → ID-24.
 * Si ambos están activos, desempata con el modelo de la tabla de datos de hijas;
 * si no hay desempate, default ID-23.
 */
export const resolvePlastyvergCamposSeleccionadosPrefix = (
  parentTask: any,
  childTasks: any[] = [],
): 'ID-23' | 'ID-24' | null => {
  const data = parentTask?.isTaskSourceMeta?.data;
  if (!data || typeof data !== 'object') return null;

  const extOn = isOtPremiumMetaFlagOn(data.Ext);
  const selOn = isOtPremiumMetaFlagOn(data.Sel);

  if (extOn && !selOn) return 'ID-23';
  if (selOn && !extOn) return 'ID-24';
  if (!extOn && !selOn) return null;

  const widestTitulo = getWidestChildModelo(childTasks)?.titulo;
  if (isId24ModeloTareaTitle(widestTitulo) && !isId23ModeloTareaTitle(widestTitulo)) return 'ID-24';
  if (isId23ModeloTareaTitle(widestTitulo) && !isId24ModeloTareaTitle(widestTitulo)) return 'ID-23';
  return 'ID-23';
};

export const pickModeloOptionByPrefix = (
  options: Array<{id: string; titulo: string}>,
  prefix: 'ID-23' | 'ID-24',
) => {
  const upperPrefix = prefix.toUpperCase();
  return (
    options.find((option) => String(option.titulo || '').toUpperCase().startsWith(upperPrefix)) || null
  );
};

const modeloCacheByTenantPrefix = new Map<string, any>();

const cacheKey = (tenantId: string | null | undefined, prefix: string) =>
  `${String(tenantId || '')}:${prefix}`;

/**
 * Modelo donde viven los "campos seleccionados".
 * En Plastyverg: solo ID-23/ID-24 (nunca widest/legacy).
 */
export const resolveParentFieldsConfigModelo = async (
  parentTask: any,
  childTasks: any[] = [],
  tenantId?: string | null,
  layoutMapKey?: PlastyvergParentFieldsMapKey | null,
): Promise<any | null> => {
  if (isPlastyvergTenant(tenantId)) {
    const prefix =
      (layoutMapKey && isPlastyvergParentFieldsMapKey(layoutMapKey) ? layoutMapKey : null) ||
      resolvePlastyvergCamposSeleccionadosPrefix(parentTask, childTasks) ||
      ((): 'ID-23' | 'ID-24' | null => {
        const widestTitulo = getWidestChildModelo(childTasks)?.titulo;
        if (isId24ModeloTareaTitle(widestTitulo) && !isId23ModeloTareaTitle(widestTitulo)) return 'ID-24';
        if (isId23ModeloTareaTitle(widestTitulo)) return 'ID-23';
        return null;
      })();

    if (!prefix) return null;

    const key = cacheKey(tenantId, prefix);
    const cached = modeloCacheByTenantPrefix.get(key);
    if (cached) return cached;

    try {
      const options = await listModeloTareasByTitlePrefixes(['ID-23', 'ID-24']);
      const match = pickModeloOptionByPrefix(options, prefix);
      if (match?.id) {
        const found = await ModeloTareaService.find(match.id);
        if (found) modeloCacheByTenantPrefix.set(key, found);
        return found;
      }
    } catch {
      return null;
    }
    return null;
  }
  return getWidestChildModelo(childTasks);
};

/** Prefijo ID-23/ID-24 a partir del título del modelo destino. */
export const plastyvergMapKeyFromModeloTitulo = (titulo?: string | null): PlastyvergParentFieldsMapKey | null => {
  const upper = String(titulo || '').toUpperCase();
  if (upper.startsWith('ID-24') || (upper.includes('ID-24') && !upper.includes('ID-23'))) return 'ID-24';
  if (upper.startsWith('ID-23') || upper.includes('ID-23')) return 'ID-23';
  return null;
};

export const invalidateParentFieldsConfigModeloCache = (
  tenantId?: string | null,
  prefix?: 'ID-23' | 'ID-24',
) => {
  if (!prefix) {
    const tenantPrefix = `${String(tenantId || '')}:`;
    Array.from(modeloCacheByTenantPrefix.keys()).forEach((key) => {
      if (key.startsWith(tenantPrefix)) modeloCacheByTenantPrefix.delete(key);
    });
    return;
  }
  modeloCacheByTenantPrefix.delete(cacheKey(tenantId, prefix));
};

/** Actualiza la entrada de cache tras persistir appFields. */
export const putParentFieldsConfigModeloCache = (tenantId: string | null | undefined, modelo: any) => {
  if (!modelo) return;
  const titulo = String(modelo?.titulo || '').toUpperCase();
  if (titulo.startsWith('ID-23')) {
    modeloCacheByTenantPrefix.set(cacheKey(tenantId, 'ID-23'), modelo);
  }
  if (titulo.startsWith('ID-24')) {
    modeloCacheByTenantPrefix.set(cacheKey(tenantId, 'ID-24'), modelo);
  }
};

export const lookupLayoutInByParentModel = (
  byParentModel: Record<string, unknown>,
  parentTask: any,
  childTasks: any[] = [],
  tenantId?: string | null,
  layoutMapKey?: PlastyvergParentFieldsMapKey | null,
): ParentFieldLayoutConfig | null => {
  if (!byParentModel || typeof byParentModel !== 'object') return null;

  const mapKey = resolveParentFieldsMapKey(parentTask, childTasks, tenantId, layoutMapKey);
  const tryKeys = isPlastyvergTenant(tenantId)
    ? layoutMapKey && isPlastyvergParentFieldsMapKey(layoutMapKey)
      ? [mapKey]
      : [mapKey, ...PLASTYVERG_PARENT_FIELDS_MAP_KEYS]
    : [
        mapKey,
        getParentConfigKey(parentTask),
        getRecordId(parentTask?.modeloTarea),
        String(parentTask?.tituloTarea || '').trim(),
      ].filter(Boolean);

  const seen = new Set<string>();
  for (const key of tryKeys) {
    const normalized = String(key);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    if (byParentModel[key] == null && byParentModel[normalized] == null) continue;
    const raw = byParentModel[key] ?? byParentModel[normalized];
    const config = normalizeParentFieldLayoutConfig(raw);
    if (config.fields.length > 0) return config;
  }

  if (!isPlastyvergTenant(tenantId)) {
    const normalizedTry = new Set(tryKeys.map((key) => String(key)));
    for (const mapEntryKey of Object.keys(byParentModel)) {
      if (!normalizedTry.has(String(mapEntryKey))) continue;
      const config = normalizeParentFieldLayoutConfig(byParentModel[mapEntryKey]);
      if (config.fields.length > 0) return config;
    }
  }

  return null;
};

/**
 * Preferencia: mapa keyed por padre (otros tenants) o ID-23/ID-24 (Plastyverg).
 * Fallback: parentQuestionKeys legacy en modelo padre.
 */
export const resolveStoredParentFieldLayout = (
  parentTask: any,
  childTasks: any[] = [],
  configModelo?: any | null,
  tenantId?: string | null,
  layoutMapKey?: PlastyvergParentFieldsMapKey | null,
): ParentFieldLayoutConfig => {
  const childModelo = configModelo ?? getWidestChildModelo(childTasks);
  const byParentModel = getTableViewConfigFromModelo(childModelo)?.parentQuestionKeysByParentModel || {};
  const fromConfigModelo = lookupLayoutInByParentModel(
    byParentModel,
    parentTask,
    childTasks,
    tenantId,
    layoutMapKey,
  );
  if (fromConfigModelo) return fromConfigModelo;

  const fromParent = getTableViewConfig(parentTask)?.parentQuestionKeys;
  return normalizeParentFieldLayoutConfig(fromParent);
};

export const childrenTasksSignature = (tasks: any[] = []) =>
  tasks
    .map((task) => getRecordId(task))
    .filter(Boolean)
    .sort()
    .join('|');

export const parentHasProcedureAndMeta = (parentTask: any) =>
  Boolean(
    parentTask &&
      Array.isArray(parentTask.listaProcedimiento) &&
      parentTask.listaProcedimiento.length > 0 &&
      parentTask.isTaskSourceMeta,
  );
